import * as React from "react";
import {
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Drawer,
    useRestoreFocusSource,
    useRestoreFocusTarget,
    Text,
    Divider,
    Spinner
} from "@fluentui/react-components";

import { Dismiss24Regular, PanelLeft16Regular } from "@fluentui/react-icons";
import { HistoryTree } from "./HistoryTree";
import { ChatSessionItem } from "../../Types/Chat/ChatSessionItem";
import { userId } from "../../App";
import { clearChatHistory, getSessionList, newChatSession } from "../../services/AssistantService";

import { useEffect, useState } from "react";

interface AssetDrawerProps {
    className?: string;
    startOpen: boolean;
    setSessionState: (sessionState: number) => void;
}
const getDateCategory = (date: Date): string => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const today = new Date(currentYear, now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(currentYear, now.getMonth(), 1);

    if (date >= today) {
        return "Today";
    } else if (date >= yesterday) {
        return "Yesterday";
    } else if (date >= startOfWeek) {
        return "This Week";
    } else if (date >= startOfMonth) {
        return "This Month";
    } else {
        return `${date.getFullYear()}`;
    }
};

export default function HistoryDrawer({ className, startOpen, setSessionState }: AssetDrawerProps) {
    const [isOpen, setIsOpen] = useState(startOpen);
    const restoreFocusTargetAttributes = useRestoreFocusTarget();
    const restoreFocusSourceAttributes = useRestoreFocusSource();
    const [update, setUpdate] = useState(false);
    const [selectedItem, setSelectedItem] = useState<number>(-1);
    const [historyItems, setHistoryItems] = React.useState<ChatSessionItem[]>([]);
    const [isLoadingSessionList, setIsLoadingSessionList] = useState(true);
    const [isLoadingNewSession, setIsLoadingNewSession] = useState(false);
    const [isInitialLoadPending, setIsInitialLoadPending] = useState(true);

    const categorizedHistory = React.useMemo(() => {
        const result: Record<string, ChatSessionItem[]> = {};

        historyItems.forEach(item => {
            const lastChangedDate = new Date(item.lastChangedAt);
            const category = getDateCategory(lastChangedDate);

            if (!result[category]) {
                result[category] = [];
            }

            result[category].push(item);
        });

        Object.keys(result).forEach(category => {
            result[category].sort((a, b) =>
                new Date(b.lastChangedAt).getTime() - new Date(a.lastChangedAt).getTime()
            );
        });

        const sortedResult: Record<string, ChatSessionItem[]> = {};
        const categoryOrder = ["Today", "Yesterday", "This Week", "This Month"];
        categoryOrder.forEach(category => {
            if (result[category] && result[category].length > 0) {
                sortedResult[category] = result[category];
            }
        });

        const yearCategories = Object.keys(result)
            .filter(category => !categoryOrder.includes(category))
            .map(category => {
                const year = parseInt(category);
                return {
                    category,
                    year: !isNaN(year) ? year : 0
                };
            })
            .sort((a, b) => b.year - a.year);
        yearCategories.forEach(({ category }) => {
            sortedResult[category] = result[category];
        });

        return sortedResult;
    }, [historyItems]);

    useEffect(() => { setIsOpen(startOpen) }, [startOpen]);

    const newSession = async () => {
        setIsOpen(false);
        setSessionState(-1);
        setSelectedItem(-1);
        setIsLoadingNewSession(true);
        let sessionId = await newChatSession(userId);
        setIsLoadingNewSession(false);
        setSessionState(sessionId);
        setSelectedItem(sessionId);
        setUpdate(!update);
    }

    const deleteSession = async (sessionId: number) => {
        const wasSelectedItem = selectedItem === sessionId;

        setHistoryItems((prevItems) => prevItems.filter(item => item.id !== sessionId));

        if (wasSelectedItem) {
            setSessionState(-1);
            setSelectedItem(-1);
        }

        try {
            await clearChatHistory(sessionId);
        } catch (error) {
            console.error("Failed to delete session:", error);
        } finally {
            setUpdate(!update);
        }
    };

    useEffect(() => {
        if (!userId) {
            setHistoryItems([]);
            setSelectedItem(-1);
            setSessionState(-1);
            setIsLoadingSessionList(false);
            return;
        }

        let isActive = true;
        setIsLoadingSessionList(true);

        getSessionList(userId).then(sessionList => {
            if (!isActive) return;

            const sortedList = sessionList.sort((a, b) =>
                new Date(b.lastChangedAt).getTime() - new Date(a.lastChangedAt).getTime()
            );
            setHistoryItems(sortedList);

            if (isInitialLoadPending) {
                if (sortedList.length > 0) {
                    setSelectedItem(sortedList[0].id);
                    setSessionState(sortedList[0].id);
                } else {
                    setSelectedItem(-1);
                    setSessionState(-1);
                }
                setIsInitialLoadPending(false);
            } else {
                if (selectedItem !== -1 && !sortedList.some(item => item.id === selectedItem)) {
                    setSelectedItem(-1);
                    setSessionState(-1);
                } else if (selectedItem === -1 && sortedList.length > 0) {
                } else if (sortedList.length === 0) {
                    setSelectedItem(-1);
                    setSessionState(-1);
                }
            }
        }).catch(error => {
            if (!isActive) return;
            console.error("Failed to load session list:", error);
            setHistoryItems([]);
            setSelectedItem(-1);
            setSessionState(-1);
        }).finally(() => {
            if (!isActive) return;
            setIsLoadingSessionList(false);
        });

        return () => {
            isActive = false;
        };
    }, [update, userId, isInitialLoadPending]);

    return (
        <div className={`${className}h-8 -z-11 drop-shadow-md`}>
            {(isLoadingSessionList || isLoadingNewSession) && <Spinner className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" size="extra-large" />}
            <div className="fixed h-screen">
                {!isOpen && (
                    <button className="h-screen w-6 bg-primary-light dark:bg-primary-dark hover:bg-opacity-75 "
                        {...restoreFocusTargetAttributes}
                        onClick={() => setIsOpen(true)}
                    >
                        <PanelLeft16Regular className="absolute top-1 left-1" />
                    </button>
                )}

                <Drawer
                    {...restoreFocusSourceAttributes}
                    type={window.innerWidth <= 768 ? "overlay" : "inline"}
                    separator
                    open={isOpen}
                    onOpenChange={(_, { open }) => setIsOpen(open)}
                    style={{ height: "100%" }}
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle
                            action={
                                <button
                                    aria-label="Close"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Dismiss24Regular />
                                </button>
                            }
                        >
                            Chat History
                        </DrawerHeaderTitle>
                        <button className="bg-primary-light dark:bg-primary-dark hover:bg-opacity-75 w-full h-10 rounded-sm" onClick={newSession}>
                            New Chat
                        </button>
                    </DrawerHeader>

                    <DrawerBody className="">
                        {Object.keys(categorizedHistory).length === 0 ? (
                            <Text className="p-4 text-center italic">{isLoadingSessionList ? "Loading chats..." : "Press new chat to start a conversation"}</Text>
                        ) : (
                            (() => {
                                const orderedCategories = [
                                    "Today",
                                    "Yesterday",
                                    "This Week",
                                    "This Month",
                                    ...Object.keys(categorizedHistory)
                                        .filter(cat => !["Today", "Yesterday", "This Week", "This Month"].includes(cat))
                                        .sort((a, b) => parseInt(b) - parseInt(a))
                                ];

                                return orderedCategories
                                    .filter(category => categorizedHistory[category])
                                    .map(category => (
                                        <div key={category} className="mb-4">

                                            <HistoryTree
                                                title={category}
                                                itemSelected={selectedItem}
                                                setItemSelected={setSelectedItem}
                                                onItemClick={setSessionState}
                                                closeDrawer={() => setIsOpen(false)}
                                                history={categorizedHistory[category]}
                                                onDeleteItem={(id) => { deleteSession(id); }}
                                            />
                                            <Divider className="mt-4" />
                                        </div>
                                    ));
                            })()
                        )}
                    </DrawerBody>
                </Drawer>
            </div>
            <div className={isOpen ? "w-80" : "w-6"} />
        </div>
    );
}